import {
  authMethodsResponseSchema,
  backupCodesResponseSchema,
  enableTwoFactorResponseSchema,
  invitationInfoResponseSchema,
  meResponseSchema,
  signInResponseSchema,
  socialSignInResponseSchema,
  type MeResponse,
} from '@pm-tool/shared';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { featuresQuery } from '@/features/feature-flags/use-feature';
import { ApiError, apiRequest } from '@/lib/api-client';

const ignored = z.unknown();

/** Signed-in user, or null without a session. */
export const meQuery = queryOptions({
  queryKey: ['auth', 'me'],
  queryFn: async ({ signal }): Promise<MeResponse | null> => {
    try {
      return await apiRequest({ path: '/v1/auth/me', schema: meResponseSchema, signal });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 60_000,
});

/**
 * After signing in (or when the session ended): reload the user and the feature flags. The
 * flags depend on the viewer (`admin` = administrators only) and were cached on the login page
 * without a session, where every `admin` flag is off.
 */
export async function refreshSession(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: meQuery.queryKey }),
    queryClient.invalidateQueries({ queryKey: featuresQuery.queryKey }),
  ]);
}

/** Sign-in methods of the login page (Microsoft only while ENTRA_* is configured). */
export const authMethodsQuery = queryOptions({
  queryKey: ['auth', 'methods'],
  queryFn: ({ signal }) =>
    apiRequest({ path: '/v1/auth/methods', schema: authMethodsResponseSchema, signal }),
  staleTime: Infinity,
});

export const invitationQuery = (token: string) =>
  queryOptions({
    queryKey: ['auth', 'invitation', token],
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/auth/invitations/${encodeURIComponent(token)}`,
        schema: invitationInfoResponseSchema,
        signal,
      }),
    staleTime: Infinity,
  });

export const authApi = {
  signIn: (email: string, password: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/sign-in/email',
      schema: signInResponseSchema,
      body: { email, password },
    }),
  /** Starts the Microsoft sign-in; the browser then leaves for the returned URL. */
  signInWithMicrosoft: (callbackURL: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/sign-in/social',
      schema: socialSignInResponseSchema,
      body: { provider: 'microsoft', callbackURL, disableRedirect: true },
    }),
  verifyTotp: (code: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/two-factor/verify-totp',
      schema: ignored,
      body: { code },
    }),
  verifyBackupCode: (code: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/two-factor/verify-backup-code',
      schema: ignored,
      body: { code },
    }),
  enableTwoFactor: (password: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/two-factor/enable',
      schema: enableTwoFactorResponseSchema,
      body: { password },
    }),
  regenerateBackupCodes: (password: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/two-factor/generate-backup-codes',
      schema: backupCodesResponseSchema,
      body: { password },
    }),
  signOut: () =>
    apiRequest({ method: 'POST', path: '/v1/auth/sign-out', schema: ignored, body: {} }),
  requestPasswordReset: (email: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/request-password-reset',
      schema: ignored,
      body: { email },
    }),
  resetPassword: (token: string, newPassword: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/reset-password',
      schema: ignored,
      body: { token, newPassword },
    }),
  acceptInvitation: (token: string, password: string) =>
    apiRequest({
      method: 'POST',
      path: '/v1/auth/invitations/accept',
      schema: ignored,
      body: { token, password },
    }),
  devSignIn: (email: string) =>
    apiRequest({ method: 'POST', path: '/v1/auth/dev/sign-in', schema: ignored, body: { email } }),
};
